import { Space, Table, Tag, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import Button from '../components/Forms/Controls/Button';
import DefaultLayout from '../components/Layouts/DefaultLayout';
import Modal from '../components/Modal';
import CreateCategoryModal from '../components/Modals/CreateCategoryModal';
import { Category, CategoryStatus, categoryStatusOptions, CategoryType } from '../dto/Category.dto';
import { useDeleteCategory, useGetCategories } from '../services/api/category';
import styles from './CategoriesPage.module.scss';

const { Text } = Typography;

interface DataType {
  key: string;
  name: string;
  type: CategoryType;
  status: CategoryStatus;
  position: number;
  ordinalPosition: number;

}

const getStatusColor = (status: CategoryStatus): string => {
  switch (status) {
    case CategoryStatus.PUBLISHED:
      return 'success';
    case CategoryStatus.HIDDEN:
      return 'default';
    default:
      return 'processing';
  }
};

const getTypeColor = (type: CategoryType): string => {
  switch (type) {
    case CategoryType.Admin:
      return 'purple';
    case CategoryType.Customer:
      return 'blue';
    default:
      return 'default';
  }
};

const CategoriesPage: React.FC = () => {
  const { Column } = Table;

  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(100);
  const [total, setTotal] = useState<number>(0);
  const [type, setType] = useState<any>(CategoryType.Admin);
  const [status, setStatus] = useState<any>(CategoryStatus.All);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>();

  const [dataSource, setDataSource] = useState<DataType[]>([]);

  const { data: categories, isLoading } = useGetCategories({
    limit: limit,
    page: page,
    type: type,
    status: status,
  });

  const { mutateAsync: deleteCategory } = useDeleteCategory();

  const handleAddCategories = () => {
    setSelectedCategory(null);
    setIsModalOpen(true);
  };

  useEffect(() => {
    if (categories?.data?.length) {
      const limit = categories.limit;
      const page = categories.currentPage;
      setTotal(categories.resultsCount);
      setDataSource(
        categories.data.map((el, index) => {
          const ordinalPosition = index + 1 + (page - 1) * limit;
          return {
            key: el.id,
            name: el.name,
            type: el.type,
            status: el.status,
            position: el.position,
            ordinalPosition: ordinalPosition,
          };
        })
      );
    }
  }, [categories]);

  return (
    <>
      <DefaultLayout>
        <main>
          <div>
            <h2 className='text-black'>Categories Page</h2>
            <Button onClick={handleAddCategories}>Add</Button>
          </div>

          <div>
            <Table
              dataSource={dataSource}
              loading={isLoading}
              pagination={{
                current: page,
                total: total,
                pageSize: limit,
                position: ['bottomCenter', 'topCenter'],
              }}
              onChange={(page, filter) => {
                setType(filter.type?.[0] ? filter.type[0] : undefined);
                setStatus(filter.status?.[0] ? filter.status[0] : undefined);
                page.current && setPage(page.current);
                page.pageSize && setLimit(page.pageSize);
              }}
              onRow={(record, rowIndex) => {
                return {
                  onClick: (event) => {
                    setSelectedCategory({
                      ...record,
                      id: record.key,
                    });
                    setIsModalOpen(true);
                  },
                };
              }}
            >
              <Column 
                title='Id' 
                dataIndex='key' 
                key='key' 
                width={'300px'}
                render={(text: string) => (
                  <Text code style={{ fontSize: '12px' }}>
                    {text}
                  </Text>
                )}
              />
              <Column
                title='Position'
                dataIndex='ordinalPosition'
                key='ordinalPosition'
                width={'200px'}
                render={(_: any, record: DataType) => (
                  <div className={styles.positionContainer}>
                    <div className={styles.positionBadge}>
                      #{record.ordinalPosition}
                    </div>
                    <div className={styles.positionValue}>
                      Pos: {record.position}
                    </div>
                  </div>
                )}
              />
              <Column title='Name' dataIndex='name' key='name' />
              <Column
                title='Type'
                dataIndex='type'
                key='type'
                width={'120px'}
                defaultFilteredValue={[CategoryType.Admin]}
                filters={[
                  {
                    text: 'Admin',
                    value: CategoryType.Admin,
                  },
                  {
                    text: 'Customer',
                    value: CategoryType.Customer,
                  },
                ]}
                filterMultiple={false}
                render={(type: CategoryType) => (
                  <Tag color={getTypeColor(type)} style={{ fontWeight: 500 }}>
                    {type === CategoryType.Admin ? 'Admin' : 'Customer'}
                  </Tag>
                )}
              />
              <Column
                title='Status'
                dataIndex='status'
                key='status'
                width={'120px'}
                defaultFilteredValue={[CategoryStatus.All]}
                filters={categoryStatusOptions}
                filterMultiple={false}
                render={(status: CategoryStatus) => (
                  <Tag color={getStatusColor(status)} style={{ fontWeight: 500 }}>
                    {status === CategoryStatus.PUBLISHED 
                      ? 'Published' 
                      : status === CategoryStatus.HIDDEN 
                      ? 'Hidden' 
                      : 'All'}
                  </Tag>
                )}
              />
              <Column
                title='Action'
                key='action'
                width={'120px'}
                render={(_: any, record: DataType) => (
                  <Space size='middle'>
                    <Button
                      onClick={(event) => {
                        event.stopPropagation();
                        const category = categories?.data.find(
                          (c) => c.id === record.key
                        );
                        setSelectedCategory(category);
                        setIsDeleteModalOpen(true);
                      }}
                    >
                      Delete{' '}
                    </Button>
                  </Space>
                )}
              />
            </Table>
          </div>
        </main>
      </DefaultLayout>
      <CreateCategoryModal
        setIsOpen={setIsModalOpen}
        isOpen={isModalOpen}
        category={selectedCategory}
      />
      <Modal
        title='Delete Category'
        content={'Are you sure you want to delete this Category?'}
        open={isDeleteModalOpen}
        onCancel={() => {
          setIsDeleteModalOpen(false);
        }}
        cancelText={'No, keep the category'}
        onOk={async () => {
          selectedCategory?.id && (await deleteCategory(selectedCategory.id));
          setIsDeleteModalOpen(false);
          setSelectedCategory(null);
        }}
        okText={'Yes, delete it'}
      />
    </>
  );
};

export default CategoriesPage;
